import { Plus } from "@phosphor-icons/react";
import { useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { type Columns, DataTable } from "@/components/DataTable";
import { FilterPills } from "@/components/FilterPills";
import { HintedButton } from "@/components/HintedButton";
import { PageHeader } from "@/components/PageHeader";
import { Panel } from "@/components/Panel";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/field";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { BulkActions } from "@/features/importexport/components/BulkActions";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import type { MasterRecord } from "@/features/records/api";
import { RecordTree } from "@/features/records/components/RecordTree";
import { recordsQuery } from "@/features/records/queries";
import {
  type StatusFilter,
  statusAfterInactiveToggle,
  statusParam,
  statusPillsFor,
} from "@/features/records/statusFilter";
import { activeSchemaQuery } from "@/features/schema/queries";
import { parseDefinition } from "@/lib/forms/compile";

/**
 * One master type's records, as a flat table or as a tree.
 *
 * Both views over the same data, and the toggle is not decoration: a flat
 * list answers "where is IN-MH-PUNE", a tree answers "what is under
 * Maharashtra", and neither is a good way to do the other's job.
 */
export const Route = createFileRoute("/_authed/records/$code/")({
  staticData: { crumbs: [{ key: "nav.records", to: "/records" }, { param: "code" }] },
  loader: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    await context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale));
    await context.queryClient.ensureQueryData(activeSchemaQuery(params.code));
  },
  component: RecordBrowser,
});

type View = "list" | "tree";

function RecordBrowser() {
  const { t } = useTranslation();
  const { code } = Route.useParams();
  const navigate = useNavigate();
  const { me, can } = useSession();
  const locale = me.resolved_locale;

  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(locale));
  const { data: schema } = useSuspenseQuery(activeSchemaQuery(code));

  const [view, setView] = useState<View>("list");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("business_key");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [status, setStatus] = useState<StatusFilter>("all");

  const masterType = index.byCode.get(code);
  const mayIncludeInactive = can("record.edit", code) || can("record.approve", code);
  const inactiveVisible = includeInactive && mayIncludeInactive;

  const query = useInfiniteQuery(
    recordsQuery(code, {
      q: search,
      sort,
      locale,
      // Asking for it without the grant is a 403, not a silent downgrade —
      // so the control is only offered to somebody it will work for.
      includeInactive: inactiveVisible,
      // Server-side, because the list is keyset-paginated with no total:
      // filtering the fetched rows would show an arbitrary subset of one
      // page and "load more" would append more rows to re-filter (DEE-74).
      status: statusParam(view, status),
    }),
  );
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

  // Sorting is restricted server-side to `business_key`, `created_at` and
  // required schema fields; offering a header the API would 400 on is
  // worse than not offering it.
  const sortable = useMemo(() => {
    const fields = ["business_key", "created_at"];
    if (schema === null) return fields;
    try {
      return [
        ...fields,
        ...parseDefinition(schema.definition)
          .filter((field) => field.required && !field.localised)
          .map((field) => field.name),
      ];
    } catch {
      return fields;
    }
  }, [schema]);

  const columns = useMemo<Columns<MasterRecord>>(
    () => [
      {
        id: "business_key",
        accessorKey: "business_key",
        header: t("records.columns.businessKey"),
        cell: ({ getValue }) => <span className="font-mono">{String(getValue())}</span>,
      },
      {
        id: "display_name",
        accessorFn: (row: MasterRecord) => row.display_name ?? "",
        header: t("records.columns.name"),
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("records.columns.status"),
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={statusOf(row.original)} />
            {/* A creator's own in-flight work, marked so they can tell it
                from what everyone else can see. */}
            {!row.original.is_current && row.original.created_by === me.user_id && (
              <span className="text-xs text-muted">{t("records.draftBadge")}</span>
            )}
          </span>
        ),
      },
      {
        id: "version",
        accessorKey: "version",
        header: t("records.columns.version"),
      },
    ],
    [t, me.user_id],
  );

  const name =
    masterType === undefined
      ? code
      : (masterType.display_name[locale] ??
        masterType.display_name[me.tenant.default_locale] ??
        code);

  const canCreate = can("record.create", code);
  const hasSchema = schema !== null;

  return (
    <>
      <PageHeader
        title={name}
        description={t("records.description")}
        actions={
          canCreate && hasSchema ? (
            <Button asChild tooltip={t("tooltips.newRecord")}>
              <Link to="/records/$code/new" params={{ code }}>
                <Plus aria-hidden="true" />
                {t("records.create")}
              </Link>
            </Button>
          ) : (
            // Present and explained, rather than absent. Two different
            // reasons land here — no grant, or a type nobody has given
            // fields yet — and "the button is missing" communicates
            // neither.
            <HintedButton
              hint={canCreate ? t("records.cannotCreateNoSchema") : t("records.cannotCreate")}
            >
              <Plus aria-hidden="true" />
              {t("records.create")}
            </HintedButton>
          )
        }
      />

      {!hasSchema && (
        <div className="mb-4 rounded-panel border border-warning bg-warning-surface p-4">
          <p className="font-medium text-warning">{t("records.noSchema.title")}</p>
          <p className="mt-1 text-sm text-foreground">{t("records.noSchema.body")}</p>
        </div>
      )}

      {/* Real tabs, per the design's underline treatment, replacing the
          `aria-pressed` button pair. Radix brings the roving focus and
          the tab/tabpanel association with it, which the old fieldset
          never had — these are two views of one thing, which is what a
          tablist means, not two independent toggles. */}
      <Tabs value={view} onValueChange={(next) => setView(next as View)}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <TabsList variant="underline" aria-label={t("records.view.label")}>
            {(["list", "tree"] as const).map((option) => (
              <TabsTrigger
                key={option}
                variant="underline"
                value={option}
                tooltip={t(`records.view.about.${option}`)}
              >
                {t(`records.view.${option}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {mayIncludeInactive && (
            <Checkbox
              tooltip={t("tooltips.includeInactive")}
              checked={includeInactive}
              onChange={(event) => {
                setIncludeInactive(event.target.checked);
                setStatus(statusAfterInactiveToggle(status, event.target.checked));
              }}
              label={t("records.includeInactive")}
            />
          )}
        </div>

        {/* Template, import and export live here rather than on a route
            or a third tab: this is where the data they act on already is,
            and List/Tree are two views of the records — bulk operations
            are not a third view of anything. Gated per action inside, so
            a viewer sees Export alone. Export receives the list's live
            filters, so it exports what is on screen. */}
        {masterType !== undefined && (
          <BulkActions
            code={code}
            masterTypeId={masterType.id}
            filters={{ q: search, includeInactive: includeInactive && mayIncludeInactive }}
          />
        )}

        <TabsContent value="list">
          {/* Above the table rather than inside its toolbar: this narrows
              what the table is *of*, where the search box narrows what is
              shown within it. The labels come from the same `status.*`
              bundle `StatusBadge` renders, so a pill and the badges beneath
              it cannot say the same thing two different ways. */}
          <FilterPills
            className="mb-3"
            name="record-status"
            label={t("records.statusFilter.label")}
            value={status}
            onChange={setStatus}
            options={statusPillsFor(inactiveVisible).map((value) => ({
              value,
              label: value === "all" ? t("records.statusFilter.all") : t(`status.${value}`),
            }))}
          />
          <DataTable
            caption={name}
            columns={columns}
            data={rows}
            // `record_id` (this version's row), not `id` (the lineage). A
            // creator with an open draft against an already-approved record
            // sees *both* versions in this list — that is the point of the
            // draft badge below — and keying on the lineage gave the two
            // rows the same React key. Found by the E2E suite's first run,
            // as a console error nothing else was watching for.
            getRowId={(row) => row.record_id}
            isLoading={query.isPending}
            isFetching={query.isFetching && !query.isFetchingNextPage}
            error={query.error}
            onRetry={() => void query.refetch()}
            hasMore={query.hasNextPage}
            isLoadingMore={query.isFetchingNextPage}
            onLoadMore={() => void query.fetchNextPage()}
            sort={{ field: sort, direction: "asc" }}
            sortableColumns={sortable}
            onSortChange={setSort}
            search={{ value: search, onChange: setSearch }}
            onClearFilters={() => {
              setSearch("");
              setStatus("all");
            }}
            activeFilters={[
              ...(search === ""
                ? []
                : [{ field: "search", label: `${t("table.search")}: ${search}` }]),
              // Listed so "clear filters" is honest about what it clears —
              // a status narrowing the set while only the search chip shows
              // reads as the table having lost rows on its own.
              ...(status === "all"
                ? []
                : [
                    {
                      field: "status",
                      label: `${t("records.statusFilter.label")}: ${t(`status.${status}`)}`,
                    },
                  ]),
            ]}
            onRowActivate={(row) =>
              void navigate({ to: "/records/$code/$recordId", params: { code, recordId: row.id } })
            }
            emptyTitle={t("records.empty.title")}
            emptyBody={t("records.empty.body")}
            emptyAction={
              canCreate && hasSchema ? (
                <Button asChild variant="secondary" size="sm" tooltip={t("tooltips.newRecord")}>
                  <Link to="/records/$code/new" params={{ code }}>
                    {t("records.create")}
                  </Link>
                </Button>
              ) : undefined
            }
          />
        </TabsContent>

        <TabsContent value="tree">
          <Panel>
            <RecordTree
              roots={rows.map((row) => ({
                id: row.id,
                label: row.display_name ?? row.business_key,
                sublabel: row.business_key,
              }))}
              rootCode={code}
              codeOf={(id) => index.byId.get(id)?.code}
              locale={locale}
              includeInactive={includeInactive && mayIncludeInactive}
              onOpen={(childCode, recordId) =>
                void navigate({
                  to: "/records/$code/$recordId",
                  params: { code: childCode, recordId },
                })
              }
            />
            {query.hasNextPage && (
              <div className="flex justify-center border-t border-border p-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void query.fetchNextPage()}
                  tooltip={t("tooltips.loadMore")}
                >
                  {t("table.loadMore")}
                </Button>
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
    </>
  );
}

/** `approved` and `draft` are the approval axis; `is_active` is a
 *  different one, and a deactivated record is still approved. The badge
 *  shows whichever the reader most needs to know. */
function statusOf(row: MasterRecord): "draft" | "pending" | "approved" | "rejected" | "inactive" {
  if (row.status === "approved" && !row.is_active) return "inactive";
  if (row.status === "pending") return "pending";
  if (row.status === "rejected") return "rejected";
  if (row.status === "draft") return "draft";
  return "approved";
}
